import pick from 'lodash/pick.js';
import uuid from 'uuid/v4.js';

import { handleResponseError } from '@/Helpers.js';
import Model from '@/models/Model.js';
import TermScheduleCourseModel from '@/models/term-schedule/TermScheduleCourseModel.js';
import TermSchedulePeriod from '@/models/term-schedule/TermSchedulePeriod.js';

export default class TermSchedulePeriodCourse extends Model {
    static entity = 'term_schedule_period_course';

    static modelName = 'TermSchedulePeriodCourse';

    static primaryKey = 'uuid';

    static fields() {
        return {
            id: this.number(null).nullable(),
            uuid: this.attr(() => uuid()),
            title: this.string(''),
            course_id: this.number(null).nullable(),
            course_template_id: this.number(null).nullable(),
            course_code_id: this.number(null).nullable(),
            term_schedule_period_id: this.attr(null),
            start_week: this.number(null).nullable(),
            start_day: this.number(null).nullable(),
            duration: this.number(null).nullable(),
            credits: this.number(null).nullable(),
            hours: this.number(null).nullable(),
            order: this.number(0),
            type: this.attr(null),
            course_start_date: this.attr(null),
            course_end_date: this.attr(null),

            ...super.fields(),

            course: this.belongsTo(TermScheduleCourseModel, 'course_id'),
            term_schedule_period: this.belongsTo(TermSchedulePeriod, 'term_schedule_period_id'),
        };
    }

    // Defaults
    get modelName() {
        return this.modelName;
    }

    static searchable() {
        return ['title'];
    }

    static resetAll() {
        return this.deleteAll();
    }

    // Utility functions
    static fromPeriodId(periodId) {
        return this.query().where('term_schedule_period_id', periodId).get();
    }

    // API - PUT
    static async sync(termPeriodCourse, data) {
        const endpoint = `/api/term-schedule-periods/${termPeriodCourse.term_schedule_period_id}/courses/${termPeriodCourse.id}`;

        try {
            const { response } = await this.api().put(endpoint, data);

            if (response.status >= 200 && response.status < 300) {
                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termPeriodUpdated',
                        termPeriodId: termPeriodCourse.term_schedule_template_period_id,
                    },
                });

                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return response;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    static async updateCourses(id, payload) {
        const endpoint = `/api/term-schedules/${id}/course-relation`;

        try {
            const { response } = await this.api().post(endpoint, payload);

            if (response.status >= 200 && response.status < 300) {
                return response;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    // API - DELETE
    static async remove(termPeriodCourse) {
        const endpoint = `/api/term-schedule-periods/${termPeriodCourse.term_schedule_period_id}/courses/${termPeriodCourse.id}`;
        try {
            const { response } = await this.api().delete(endpoint);

            if (response.status >= 200 && response.status < 300) {
                await this.delete(termPeriodCourse.uuid);

                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return response;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    static async addNewCourses(termSchedulePeriod, courses) {
        const requestData = await Promise.all(
            courses.map(async (course, index) => {
                const structure = {
                    title: course.name,
                    course_code_id: course.id,
                    term_schedule_period_id: termSchedulePeriod.id,
                    order: index,
                    type: 'course',
                    start_week: 1,
                    duration: 1,
                };

                const newCourse = await new TermSchedulePeriodCourse(structure).$save();

                return pick(newCourse, [
                    'uuid',
                    'title',
                    'course_code_id',
                    'term_schedule_period_id',
                    'order',
                    'type',
                    'start_week',
                    'duration',
                ]);
            })
        );

        await this.syncNewCourses(termSchedulePeriod, requestData);

        return true;
    }

    static async syncNewCourses(termTemplatePeriod, courses) {
        const endpoint = `/api/term-schedule-periods/${termTemplatePeriod.id}/courses`;

        try {
            const { response } = await TermSchedulePeriodCourse.api().post(endpoint, {
                courses,
            });

            if (response.status >= 200 && response.status < 300) {
                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termPeriodUpdated',
                    },
                });

                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return true;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    static async reorder(termPeriodId, updatedCourses) {
        const endpoint = `/api/term-schedule-periods/${termPeriodId}/courses/reorder`;
        try {
            const { response } = await this.api().post(endpoint, {
                courses: updatedCourses,
            });

            if (response.status >= 200 && response.status < 300) {
                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termPeriodUpdated',
                        termPeriodId,
                    },
                });

                await this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return response;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    static async addNewBreak(termSchedulePeriod) {
        const index = TermSchedulePeriodCourse.query()
            .where('term_schedule_period_id', termSchedulePeriod.id)
            .get().length;
        const requestData = [];

        const courseBreak = await new TermSchedulePeriodCourse({
            title: 'Break',
            course_code_id: null,
            term_schedule_period_id: termSchedulePeriod.id,
            order: index,
            type: 'break',
        }).$save();

        requestData.push(
            pick(courseBreak, ['uuid', 'title', 'course_code_id', 'term_schedule_period_id', 'order', 'type'])
        );

        await this.syncNewCourses(termSchedulePeriod, requestData);

        return true;
    }
}
